import { AlpineData, TSelectArray, TSelectList, TSelectObject, TSelectValue, TSelectValues } from "@/js/interfaces/Components/alpinejs/SelectMultiple";
import is from "@/js/app/support/is";
import { InvalidArgumentException } from '@/app/exceptions';
import { createAlpineLivewireComponent } from "@nolikein/typed-alpinejs";

/**
 * Le composant permet de gérer 2 cas, soit une liste sous forme d'array, soit une liste sous forme d'object.
 *
 * Pour un array [1, 2, 3] :
 * Labels affichés : 1, 2, 3
 * defaultValues reçu : [2, 3] (les valeurs)
 * Valeurs envoyées à Livewire : [2, 3] (les valeurs)
 * En interne values doit stocker les index pour retrouver les valeurs
 *
 * Pour un object {a: 1, b: 2, c: 3} :
 * Labels affichés : 1, 2, 3 (les valeurs de l'objet)
 * defaultValues reçu : ['a', 'c'] (les clés)
 * Valeurs envoyées à Livewire : ['a', 'c'] (les clés)
 * En interne values stocke les clés
 */

export default {
    name: 'SelectMultiple',
    component: (
        livewireData: string,
        initList: TSelectList = [],
        defaults: TSelectValues = [],
        disabled: boolean = false,
        max: number = -1,
    ) => createAlpineLivewireComponent<AlpineData, Record<string, unknown>, object>({
        open: false,
        search: '',
        values: [],
        defaultValues: [],
        list: initList,
        livewireData: livewireData,
        disabled: disabled,
        isListArray: false,

        /**
         * Initialize the component
         */
        init() {
            if (!is.array(defaults)) {
                throw new InvalidArgumentException('The default values should be an array');
            }

            // Sanitize the list
            if (is.array(initList)) {
                this.isListArray = true;
                // Pour un array, defaultValues contient les valeurs, on doit trouver leurs index
                if (defaults.length > 0) {
                    const arrayKeys = this.getArrayKeys(initList, defaults);
                    this.values = arrayKeys;
                    this.defaultValues = arrayKeys;
                } else {
                    this.values = [];
                    this.defaultValues = [];
                }

            } else if (is.object(initList)) {
                this.isListArray = false;
                // Pour un object, defaultValues contient déjà les clés
                if (defaults.length > 0) {
                    this.values = structuredClone(defaults);
                    this.defaultValues = structuredClone(defaults);
                } else {
                    this.values = [];
                    this.defaultValues = [];
                }

            } else {
                throw new InvalidArgumentException('The given list MUST be either an array or a Javascript object');
            }

            this.$watch('values', (values: unknown) => {
                if (is.array(values) && values.every(val => is.string(val) || is.number(val))) {
                    this.publish();
                }
            });
        },

        getList(): TSelectList {
            if ('' === this.search) {
                return this.list;
            }
            const filtered: TSelectList = this.isListArray ? [] : {};
            if (this.isListArray) {
                (this.list as TSelectArray).forEach((item) => {
                    const itemStr = is.number(item) ? item.toString() : item;
                    if (itemStr.toLowerCase().includes(this.search.toLowerCase().trim())) {
                        (filtered as TSelectArray).push(item);
                    }
                });
            } else {
                for (const [key, value] of Object.entries(this.list as TSelectObject)) {
                    const valueStr = is.number(value) ? value.toString() : value;
                    if (valueStr.toLowerCase().includes(this.search.toLowerCase().trim())) {
                        (filtered as TSelectObject)[key] = value;
                    }
                }
            }
            return filtered;

        },

        /**
         * Detect if should add or remove a value
         * @param value The value to add/remove
         */
        addOrRemoveValue(value: TSelectValue) {
            if (!this.values.includes(value)) {
                if (-1 === max || this.values.length < max) {
                    this.addValue(value);
                }
            } else {
                this.removeValue(value);
            }
        },

        /**
         * Add a value in the select bag
         * @param value The value to add in our set of values
         */
        addValue(value: TSelectValue) {
            if (!this.values.includes(value)) {
                this.values.push(value);
            }
        },

        /**
         * Remove a value from the select bag
         * @param value The value to remove from the select bag
         */
        removeValue(value: TSelectValue) {
            if (this.values.includes(value)) {
                const index = this.values.indexOf(value);
                this.values.splice(index, 1);
            }
        },

        /**
         * Checks if a value is already selected
         * @param value The value to check
         */
        isSelected(value: TSelectValue): boolean {
            return this.values.includes(value);
        },

        /**
         * Update our value into livewire (the backend)
         */
        publish() {
            if (!this.disabled) {
                this.$wire.$set(this.livewireData, this.isSelectionEmpty() ? [] : this.getValues());
            }
        },

        /**
         * Reset the values bag.
         */
        reset() {
            this.values = [...this.defaultValues];
        },

        /**
         * Toggle the display of the the selection menu.
         */
        toggleDisplaySelectionMenu(): void {
            if (!this.disabled) {
                this.open = !this.open;

                // Remove search on open/close
                this.search = '';

                // Focus the search input if exists
                if (undefined !== this.$refs.searchInput) {
                    this.$nextTick(() => {
                        this.$refs.searchInput.focus();
                    });
                }
            }
        },

        /**
         * Tells the action to do when the form input is enabled.
         */
        onEnable(): void {
            this.disabled = false;

        },

        /**
         * Tells the action to do when the form input is disabled.
         */
        onDisable(): void {
            this.disabled = true;
        },

        /**
         * Retrieve the labels for all selected values.
         */
        getValuesLabels(): Array<string> {
            return this.values.map((value) => {
                const label = this.isListArray
                    ? (this.list as TSelectArray)[value as number]
                    : (this.list as TSelectObject)[value];
                // Transtype the value to have only string
                return is.number(label) ? label.toString() : label as string;
            });
        },

        /**
         * Retrieve the actual values to send to Livewire.
         */
        getValues(): TSelectValues {
            if (this.isListArray) {
                // Pour un array, on retourne les valeurs (pas les index)
                return this.values.map((index) => (this.list as TSelectArray)[index as number]);
            } else {
                // Pour un object, on retourne les clés
                return this.values;
            }
        },

        /**
         * Checks if the component selection is empty.
         */
        isSelectionEmpty(): boolean {
            return 0 === this.values.length;
        },

        /**
         * Get array indices from given values
         */
        getArrayKeys(list: TSelectArray, values: TSelectValues): Array<number> {
            const arrayKeys: Array<number> = [];

            for (const value of values) {
                const arrayKey = list.indexOf(value);
                if (-1 === arrayKey) {
                    throw new InvalidArgumentException(`The given default value "${value}" does not exist in the list provided`);
                }
                arrayKeys.push(arrayKey);
            }

            return arrayKeys;
        },
    }),
};
